import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { useAuth, apiClient } from '@zaogig/auth';

// ─── Inline data fallback ────────────────────────
const mockUser = {
  id: 'mock-user-1',
  name: '张三',
  completedJobs: 128,
  rating: 4.8,
  skills: ['活动执行', '门店导购', '展会兼职'],
};

const workerProfile = {
  name: "张三",
  avatar: "张",
  creditLevel: "A",
  orders: 128,
  income: "¥4,280",
  arrivalRate: "97%",
  rating: "4.8",
  preferences: ["活动执行", "门店导购", "展会兼职"],
  verifications: ["实名认证", "声纹认证", "GPS 打卡", "设备正常"],
  menu: ["我的排班", "收入记录", "信用记录", "能力标签", "申诉与客服", "设置"],
};
void workerProfile;

type BadgeTone = "gray" | "green" | "blue" | "dark" | "amber" | "red";

function Badge({ children, tone = "gray" }: { children: ReactNode; tone?: BadgeTone }) {
  const tones: Record<BadgeTone, string> = {
    gray: "bg-gray-50 text-gray-600 ring-gray-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    dark: "bg-gray-950 text-white ring-gray-950",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    red: "bg-red-50 text-red-700 ring-red-100",
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-3xl bg-white p-4 shadow-sm ring-1 ring-gray-100 ${className}`}>
      {children}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="min-h-[88px]">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-2 text-lg font-black text-gray-950">{value}</div>
    </Card>
  );
}

function MenuList({ items, onNavigate }: { items: string[]; onNavigate?: (item: string) => void }) {
  const handleClick = (item: string) => {
    const menuAction: Record<string, { message: string; tab?: string }> = {
      '我的排班': { message: '我的排班将在「面试」Tab 中展示', tab: 'interview' },
      '收入记录': { message: '请在小程序「收入」页面查看已结算收入与争议记录' },
      '信用记录': { message: '当前信用分已展示在本页，详细记录由后台信用档案同步' },
      '能力标签': { message: '能力标签已展示在「接单偏好」区域，完成任务后自动更新' },
      '申诉与客服': { message: '如需申诉，请从对应考勤或结算记录进入处理' },
      '设置': { message: '请在浏览器或微信权限设置中管理定位、麦克风和通知权限' },
    };
    const action = menuAction[item] || { message: '请从首页、岗位、企业或个人中心进入对应业务页面' };
    toast.info(action.message);
    if (action.tab && onNavigate) {
      onNavigate(action.tab);
    }
  };

  return (
    <Card>
      <div className="space-y-1">
        {items.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => handleClick(item)}
            className="flex w-full items-center justify-between rounded-2xl px-3 py-3 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <span>{item}</span>
            <span className="text-gray-300">›</span>
          </button>
        ))}
      </div>
    </Card>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [userStats, setUserStats] = useState<any>(null);
  const [workerProfile, setWorkerProfile] = useState<any>(null);

  const displayUser = userInfo || mockUser;

  // 合并数据，优先使用真实数据，回退到原型数据
  const data = {
    name: displayUser.name,
    avatar: displayUser.name?.[0] || '张',
    creditLevel: workerProfile?.credit_level || "A",
    recommendationLevel: workerProfile?.recommendation_level || "normal",
    creditScore: workerProfile?.credit_score || 85,
    orders: displayUser.completedJobs || 0,
    income: userStats?.totalEarnings ? `¥${userStats.totalEarnings.toLocaleString()}` : "¥4,280",
    arrivalRate: workerProfile?.arrival_rate ? `${Math.round(workerProfile.arrival_rate * 100)}%` : userStats?.attendanceRate ? `${userStats.attendanceRate}%` : "97%",
    rating: (displayUser.rating || 4.8).toFixed(1),
    preferences: workerProfile?.skill_tags?.length > 0 ? workerProfile.skill_tags : (displayUser.skills && displayUser.skills.length > 0) ? displayUser.skills : ["活动执行", "门店导购", "展会兼职"],
    riskTags: workerProfile?.risk_tags || [],
    recommendReason: workerProfile?.recommend_reason || [],
    verifications: ["实名认证", "声纹认证", "GPS 打卡", "设备正常"],
    menu: ["我的排班", "收入记录", "信用记录", "能力标签", "申诉与客服", "设置"],
  };

  // 获取推荐等级的显示文本
  const getRecommendationLevelText = (level: string) => {
    const levelMap: Record<string, string> = {
      'priority': '优先推荐',
      'normal': '正常推荐',
      'observe': '观察期',
      'limited': '限制接单'
    };
    return levelMap[level] || '正常推荐';
  };

  // 获取推荐等级的渐变色
  const getRecommendationGradient = (level: string) => {
    const gradientMap: Record<string, string> = {
      'priority': 'from-emerald-500 to-blue-600',
      'normal': 'from-blue-500 to-indigo-600',
      'observe': 'from-amber-500 to-orange-600',
      'limited': 'from-red-500 to-rose-600'
    };
    return gradientMap[level] || 'from-blue-500 to-indigo-600';
  };

  // 加载用户数据
  useEffect(() => {
    async function loadUserData() {
      try {
        setLoading(true);

        // 获取用户信息
        try {
          const info = await apiClient.get('/v1/auth/me');
          setUserInfo(info);
        } catch (err) {
          console.log('使用模拟用户数据');
        }

        // 获取用户统计
        try {
          const stats = await apiClient.get('/v1/user/stats');
          setUserStats(stats);
        } catch (err) {
          console.log('使用模拟统计数据');
        }

        // 获取用户画像
        const profileId = user?.accountId || userInfo?.id;
        if (profileId) {
          try {
            const profile = await apiClient.get(`/v1/profiles/${profileId}`);
            setWorkerProfile(profile);
          } catch (err) {
            console.log('使用模拟画像数据');
          }
        }
      } catch (err) {
        console.error('加载用户数据失败:', err);
      } finally {
        setLoading(false);
      }
    }

    loadUserData();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] p-5 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[760px] bg-[#F7F9FC]">
      <div className="bg-white px-5 py-4">
        <p className="text-xs text-gray-400">员工端 · 微信登录</p>
        <h2 className="mt-1 text-lg font-black text-gray-950">个人中心</h2>
      </div>
      <div className="space-y-4 p-5">
        <Card>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-xl font-black text-emerald-700">
              {data.avatar}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-black text-gray-950">{data.name}</h3>
                <Badge tone="green">信用 {data.creditLevel}</Badge>
              </div>
              <p className="mt-1 text-sm text-gray-500">接单 {data.orders} 次 · 可信档案已建立</p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-3">
          <MetricCard label="本月收入" value={data.income} />
          <MetricCard label="到岗率" value={data.arrivalRate} />
          <MetricCard label="评分" value={data.rating} />
        </div>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div className="font-black text-gray-950">接单偏好</div>
            <Badge tone="blue">影响推荐</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.preferences.map((item: string) => (
              <Badge key={item}>{item}</Badge>
            ))}
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div className="font-black text-gray-950">可信认证</div>
            <Badge tone="green">可接单</Badge>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {data.verifications.map((item) => (
              <div key={item} className="rounded-2xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
                ✓ {item}
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div className="font-black text-gray-950">信用资产</div>
            <Badge tone="dark">信用分 {data.creditScore}</Badge>
          </div>
          <div className={`rounded-2xl bg-gradient-to-br ${getRecommendationGradient(data.recommendationLevel)} p-4 text-white`}>
            <div className="text-sm opacity-80">AI 综合推荐等级</div>
            <div className="mt-1 text-2xl font-black">{getRecommendationLevelText(data.recommendationLevel)}</div>
            <p className="mt-2 text-sm opacity-80">
              {data.recommendReason?.length > 0 ? data.recommendReason.join('、') : '距离近、准时率高、同类任务经验稳定。'}
            </p>
          </div>
        </Card>

        {data.riskTags.length > 0 && (
          <Card>
            <div className="mb-3 flex items-center justify-between">
              <div className="font-black text-gray-950">风险提示</div>
              <Badge tone={data.riskTags.length > 2 ? "red" : "amber"}>需注意</Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.riskTags.map((tag: string) => (
                <div key={tag} className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-100">
                  ! {tag}
                </div>
              ))}
            </div>
          </Card>
        )}

        <MenuList items={data.menu} />
      </div>
    </div>
  );
}
